import { useEffect, useMemo, useState } from "react";
import { Drawer, Flex, Button, Divider } from "antd";

import { CloseOutlined } from "@ant-design/icons";
import ChevronLeftIcon from "@/icons/ChevronLeftIcon";
import ChevronRightIcon from "@/icons/ChevronRightIcon";

import SearchPanel from "./SearchPanel";
import ObjectPanel from "./ObjectPanel";

import styles from "./right-drawer.module.css";

const RightPanelDrawer = ({
  isOpen,
  onClose,
  isSearchOpen,
  isObjectOpen,
  setIsObjectOpen,
  currentObject,
  isObjectLoading,
  isResultsLoading,
  searchValue,
  searchInSelectedDefinitions,
  onSearchInSelectedDefinitionsChange,
  isClickResult = false,
  onCancelMapClick,
  onSearch,
  onApplySearch,
  searchResults = [],
  onSelectObject,
  definitionValue,
  definitionsOptions,
  onDefinitionChange,
  isDefinitionRequired,
  minimized,
  onToggleMinimize,
  isLoading = false,
}) => {
  const baseSearchWidth = 200;
  const baseObjectWidth = 400;
  const controlsWidth = 48;

  const minDrawerWidth = minimized
    ? 20
    : controlsWidth +
      (isSearchOpen ? baseSearchWidth : 0) +
      (isObjectOpen ? baseObjectWidth : 0);

  const [searchPanelWidth, setSearchPanelWidth] = useState(baseSearchWidth);
  const [objectPanelWidth, setObjectPanelWidth] = useState(baseObjectWidth);

  const [size, setSize] = useState(500);
  const [activeTabKey, setActiveTabKey] = useState();

  const { definition } = currentObject || {};

  const groupsFields = useMemo(() => {
    return (
      definition?.groups
        ?.filter(({ fields }) => !!fields && !!fields.length)
        .sort((a, b) => a.idx - b.idx) || []
    );
  }, [definition]);

  const drawerWidth = minimized ? 20 : size;

  useEffect(() => {
    if (groupsFields?.length) {
      setActiveTabKey(groupsFields[0].uuid);
    }
  }, [definition?.uuid, groupsFields]);

  // Ресайз
  const handleResize = (newSize) => {
    if (minimized) {
      return;
    }

    const nextSize = Math.max(newSize, minDrawerWidth);
    const available = nextSize - controlsWidth;

    if (isObjectOpen && isSearchOpen) {
      const newObjectWidth = available - searchPanelWidth;
      setObjectPanelWidth(Math.max(newObjectWidth, baseObjectWidth));
    } else if (isSearchOpen) {
      setSearchPanelWidth(Math.max(available, baseSearchWidth));
    }

    setSize(nextSize);
  };

  useEffect(() => {
    const newSize =
      controlsWidth +
      (isSearchOpen ? searchPanelWidth : 0) +
      (isObjectOpen ? objectPanelWidth : 0);

    setSize(newSize);
  }, [isSearchOpen, isObjectOpen, searchPanelWidth, objectPanelWidth]);

  return (
    <Drawer
      closable={false}
      destroyOnHidden
      onClose={onClose}
      open={isOpen}
      size={drawerWidth}
      mask={false}
      maskClosable={false}
      className={styles.right_drawer}
      styles={{
        body: {
          padding: minimized ? 0 : 24,
          overflow: "hidden",
        },
      }}
      resizable={
        !minimized && {
          onResize: handleResize,
        }
      }
      extra={null}
    >
      {/* Боковые контролы */}
      <Flex vertical gap={8} className={styles.controls}>
        <Button
          size="large"
          className={styles.button}
          icon={<CloseOutlined />}
          onClick={() => {
            if (!minimized && isSearchOpen && isObjectOpen) {
              setIsObjectOpen(false);
            } else {
              onClose();
            }
          }}
        />
        <Button
          size="large"
          className={styles.button}
          icon={minimized ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          onClick={() => onToggleMinimize?.()}
        />
      </Flex>

      <Flex style={{ height: "100%" }}>
        {!minimized && isObjectOpen && (
          <div style={{ width: objectPanelWidth }}>
            <ObjectPanel
              activeTabKey={activeTabKey}
              setActiveTabKey={setActiveTabKey}
              currentObject={currentObject}
              isLoading={isObjectLoading}
            />
          </div>
        )}

        {isSearchOpen && isObjectOpen && !minimized && (
          <Divider orientation="vertical" className={styles.divider} />
        )}

        {!minimized && isSearchOpen && (
          <div style={{ width: searchPanelWidth, marginLeft: "auto" }}>
            <SearchPanel
              onSearch={onSearch}
              onApplySearch={onApplySearch}
              searchResults={searchResults}
              onSelectObject={onSelectObject}
              definitionValue={definitionValue}
              definitionsOptions={definitionsOptions}
              onDefinitionChange={onDefinitionChange}
              isDefinitionRequired={isDefinitionRequired}
              searchValue={searchValue}
              searchInSelectedDefinitions={searchInSelectedDefinitions}
              onSearchInSelectedDefinitionsChange={
                onSearchInSelectedDefinitionsChange
              }
              isClickResult={isClickResult}
              onCancelMapClick={onCancelMapClick}
              isLoading={isLoading || isResultsLoading}
              isObjectOpen={isObjectOpen}
            />
          </div>
        )}
      </Flex>
    </Drawer>
  );
};

export default RightPanelDrawer;
